import { Box, Skeleton } from '@mui/material';
import { insightsRegistry } from '../insights-registry';
import { insightRendererMap } from '../registry/insightRendererMap';
import { useInsight } from '../hooks/useInsight';
import type { Filters, InsightResult, NarrativeBlockData } from '../types';
import { useMetricsFrameworkContext } from '../context/MetricsFrameworkContext';
import TileErrorCard from './TileErrorCard';

type Props = {
  id: string;
  filters?: Filters;
  /** Hide in-card title when section header already shows display_name. */
  hideTitle?: boolean;
};

const withoutLiftedTitle = (data: InsightResult): InsightResult => {
  if ('headline' in data) {
    const narrative = data as NarrativeBlockData;
    return { ...narrative, display_name: undefined, headline: undefined };
  }
  return { ...data, display_name: undefined };
};

export default function Insight({ id, filters: filtersProp, hideTitle }: Props) {
  const { filters: contextFilters } = useMetricsFrameworkContext();
  const filters = filtersProp ?? contextFilters;
  const config = insightsRegistry[id];
  const { data, isLoading, error, statusCode } = useInsight(id, filters);

  if (!config) {
    return null;
  }

  if (error && !isLoading) {
    return (
      <Box sx={{ width: '100%', height: '100%' }}>
        <TileErrorCard
          message={error}
          statusCode={statusCode}
          height={200}
          title={hideTitle ? undefined : config.label}
          query={data?.meta?.query}
          registry={data?.meta?.registry}
          renderer={config.renderer}
        />
      </Box>
    );
  }

  if (isLoading || !data) {
    return (
      <Box sx={{ width: '100%' }}>
        <Skeleton variant="rounded" height={200} sx={{ borderRadius: 2, width: '100%' }} />
      </Box>
    );
  }

  const Renderer = insightRendererMap[config.renderer];
  const displayData = hideTitle ? withoutLiftedTitle(data) : data;
  return <Renderer data={displayData} />;
}
